/**
 * WCAG 2.2 colour contrast.
 *
 * Relative luminance (WCAG 2.2, "relative luminance" in the glossary):
 *
 *     L = 0.2126 R + 0.7152 G + 0.0722 B
 *
 * where each sRGB channel c (0–1) is linearised with c ≤ 0.04045 ? c / 12.92 :
 * ((c + 0.055) / 1.055)^2.4. WCAG 2.0 and 2.1 printed 0.03928 as the threshold, a
 * rounding left over from an early sRGB draft; the glossary now says 0.04045 and notes
 * that the difference changes no 8-bit result. This module uses 0.04045.
 *
 * Contrast ratio = (L1 + 0.05) / (L2 + 0.05), L1 the lighter. A ratio is never rounded up
 * to pass: 4.4999 fails 4.5. `truncateRatio` therefore floors the displayed value to two
 * decimals, so a failing 4.4999 reads as 4.49 rather than a misleading 4.50.
 *
 * Success criteria (WCAG 2.2): 1.4.3 Contrast (Minimum, AA) 4.5:1, or 3:1 for large text;
 * 1.4.6 Contrast (Enhanced, AAA) 7:1, or 4.5:1 for large text; 1.4.11 Non-text Contrast
 * (AA) 3:1 for UI components and meaningful graphics. Large text is at least 18 pt, or
 * 14 pt bold.
 */

import {
  clamp,
  hslToRgb,
  type Rgb,
  rgbToHsl,
  srgbToLinear,
  toHex,
} from "@/lib/tool-kit/color-charts/color-space";

/** WCAG relative luminance of an opaque sRGB colour, 0 (black) to 1 (white). */
export function relativeLuminance({ r, g, b }: Rgb): number {
  return (
    0.2126 * srgbToLinear(Math.round(r) / 255) +
    0.7152 * srgbToLinear(Math.round(g) / 255) +
    0.0722 * srgbToLinear(Math.round(b) / 255)
  );
}

/** The contrast ratio (1–21) between two opaque colours, either way round. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** The ratio floored to two decimals, so a near miss is never displayed as a pass. */
export function truncateRatio(ratio: number): number {
  return Math.floor(ratio * 100 + 1e-9) / 100;
}

/** Lay `top` over `under` (both sRGB, alpha 0–1) and return the opaque result. */
export function composite(top: Rgb, under: Rgb): Rgb {
  const a = clamp(top.a, 0, 1);
  return {
    r: top.r * a + under.r * (1 - a),
    g: top.g * a + under.g * (1 - a),
    b: top.b * a + under.b * (1 - a),
    a: 1,
  };
}

const WHITE: Rgb = { r: 255, g: 255, b: 255, a: 1 };

/**
 * Make a translucent pair measurable: the background is laid over white, then the text
 * over that. This is a choice, not a WCAG rule — the page behind a translucent
 * background is unknown to the tool.
 */
export function flatten(foreground: Rgb, background: Rgb) {
  const bg = composite(background, WHITE);
  return { fg: composite(foreground, bg), bg };
}

export type CheckId =
  | "normalAA"
  | "normalAAA"
  | "largeAA"
  | "largeAAA"
  | "uiAA";

export interface Check {
  id: CheckId;
  required: number;
  pass: boolean;
}

export const REQUIRED_RATIO: Record<CheckId, number> = {
  normalAA: 4.5,
  normalAAA: 7,
  largeAA: 3,
  largeAAA: 4.5,
  uiAA: 3,
};

export const CHECK_ORDER: readonly CheckId[] = [
  "normalAA",
  "normalAAA",
  "largeAA",
  "largeAAA",
  "uiAA",
];

/** Compare the exact (unrounded) ratio with each requirement. */
export function wcagChecks(ratio: number): Check[] {
  return CHECK_ORDER.map((id) => ({
    id,
    required: REQUIRED_RATIO[id],
    pass: ratio >= REQUIRED_RATIO[id],
  }));
}

/* ------------------------------------------------------------- suggestions */

export type Role = "foreground" | "background";

const STEP = 0.25;

function passes(candidate: Rgb, other: Rgb, target: number): boolean {
  return contrastRatio(candidate, other) >= target;
}

/**
 * The closest colour to `color`, keeping its hue and saturation and changing only its
 * HSL lightness, that reaches `target` against `other`. "Closest" is the smallest
 * lightness change in either direction. Null when no lightness can reach the target —
 * 7:1 against a mid grey, for instance, is impossible for the other colour to meet by
 * lightness alone.
 *
 * Every candidate is rounded to 8-bit before it is tested, so the returned colour passes
 * as the hex code the reader will copy, not only as an ideal real number.
 */
export function nearestPassing(
  color: Rgb,
  other: Rgb,
  target: number
): Rgb | null {
  const { h, s, l } = rgbToHsl(color);
  for (let delta = STEP; delta <= 100; delta += STEP) {
    for (const lightness of [l - delta, l + delta]) {
      if (lightness < 0 || lightness > 100) {
        continue;
      }
      const raw = hslToRgb({ h, s, l: lightness });
      const candidate = {
        r: Math.round(raw.r),
        g: Math.round(raw.g),
        b: Math.round(raw.b),
        a: 1,
      };
      if (passes(candidate, other, target)) {
        return candidate;
      }
    }
  }
  return null;
}

export interface Suggestion {
  color: Rgb;
  hex: string;
  ratio: number;
}

export function suggest(
  role: Role,
  foreground: Rgb,
  background: Rgb,
  target: number
): Suggestion | null {
  const found =
    role === "foreground"
      ? nearestPassing(foreground, background, target)
      : nearestPassing(background, foreground, target);
  if (!found) {
    return null;
  }
  const ratio =
    role === "foreground"
      ? contrastRatio(found, background)
      : contrastRatio(foreground, found);
  return { color: found, hex: toHex(found), ratio };
}
